import { useState } from 'react';
import { AlertTriangle, Loader2, ShieldAlert, UserCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

// Target-v2 Assignment Problem queue (dark). Rendered only when the server
// returns target problems, so no v1 operations surface changes.
//
// A problem is a report, not a decline. Resolving it is an explicit, reasoned,
// version-bound decision: either the commitment stands, or dispatch is requeued.
// Confirming an emergency or vehicle failure is a distinct, weightier action
// than simply reassigning, because it attests to something the Rider claimed.

const REASON_LABELS = {
  VEHICLE_ISSUE: 'Vehicle problem',
  MEDICAL_EMERGENCY: 'Medical emergency',
  SAFETY_EMERGENCY: 'Safety emergency',
  DEVICE_CONNECTIVITY: 'Phone or signal problem',
  TEMPORARY_DELAY: 'Temporary delay',
  OTHER: 'Other',
};

const RESOLUTIONS = [
  { code: 'KEEP_ASSIGNED', label: 'Keep it assigned', hint: 'The Rider continues this delivery.' },
  { code: 'REASSIGN', label: 'Reassign to someone else', hint: 'Cancels this commitment and returns the work to dispatch.' },
  { code: 'CONFIRM_VEHICLE_AND_REASSIGN', label: 'Confirm vehicle failure and reassign', hint: 'Records that you verified the vehicle is unusable.' },
  { code: 'CONFIRM_EMERGENCY_AND_REASSIGN', label: 'Confirm emergency and reassign', hint: 'Records that you verified a medical or safety emergency.' },
];

const buttonClass = 'min-h-11 rounded-xl px-4 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-not-allowed disabled:opacity-45';
const fieldClass = 'mt-1 min-h-11 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:border-neutral-700 dark:bg-neutral-900 dark:text-white';

function ProblemRow({ problem, busy, onResolve }) {
  const [open, setOpen] = useState(false);
  const [resolutionCode, setResolutionCode] = useState('KEEP_ASSIGNED');
  const [reason, setReason] = useState('');
  const canSubmit = reason.trim().length >= 10;
  const queued = problem.status === 'REASSIGNMENT_QUEUED';

  return (
    <li className="rounded-2xl border border-neutral-200 p-4 dark:border-neutral-800">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold">{REASON_LABELS[problem.reasonCode] || 'Reported delivery problem'}</p>
          <p className="mt-1 text-xs text-neutral-500">
            {problem.orderReference || `Parcel #${problem.shipmentId}`} · {problem.riderName || `Assignment #${problem.assignmentId}`}
            {problem.reportedAt ? ` · reported ${new Date(problem.reportedAt).toLocaleString()}` : ''}
          </p>
          {problem.shipmentId && <Link to={`/business/delivery?view=parcels&parcel=${problem.shipmentId}`} className="mt-2 inline-flex min-h-9 items-center text-sm font-semibold underline underline-offset-4">View parcel</Link>}
          {problem.note && (
            <p className="mt-2 break-words rounded-xl bg-neutral-50 p-3 text-sm leading-6 dark:bg-neutral-900">
              {problem.note}
            </p>
          )}
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
            queued
              ? 'bg-warning-subtle text-warning'
              : 'bg-danger-subtle text-danger'
          }`}
        >
          {queued ? 'Reassignment queued' : 'Needs a decision'}
        </span>
      </div>

      {problem.decisionOutcome === 'AUTHORIZED_REVIEW' && !queued && (
        <p className="mt-3 flex items-start gap-2 text-sm text-neutral-500">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          The Rider reported this; the system did not verify it. The delivery stays assigned
          until you decide.
        </p>
      )}

      {queued ? null : !open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`business-primary-action ${buttonClass} mt-3 bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-active`}
        >
          Decide
        </button>
      ) : (
        <div className="mt-3 grid gap-3">
          <fieldset>
            <legend className="text-xs font-medium text-neutral-600 dark:text-neutral-300">Decision</legend>
            <div className="mt-2 grid gap-2">
              {RESOLUTIONS.map((option) => (
                <label key={option.code} className="flex min-h-11 items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name={`resolution-${problem.id}`}
                    value={option.code}
                    checked={resolutionCode === option.code}
                    onChange={(event) => setResolutionCode(event.target.value)}
                    className="mt-1 h-4 w-4 accent-accent"
                  />
                  <span>
                    <span className="font-medium">{option.label}</span>
                    <span className="block text-xs text-neutral-500">{option.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <label className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
            Reason (recorded, at least 10 characters)
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value.slice(0, 500))}
              rows="3"
              maxLength="500"
              className={`${fieldClass} py-2`}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setReason(''); }}
              className={`${buttonClass} border border-neutral-300 dark:border-neutral-700`}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busy || !canSubmit}
              onClick={() => onResolve(problem, { resolutionCode, reason: reason.trim() })}
              className={`business-primary-action ${buttonClass} bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-active`}
            >
              {busy ? 'Saving…' : 'Record decision'}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

export default function AssignmentProblemQueue({ problems, loading, error, busy, onResolve, onRetry }) {
  if (loading) {
    return (
      <div role="status" className="grid min-h-32 place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent motion-reduce:animate-none" aria-hidden="true" />
        <span className="sr-only">Loading reported problems…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div role="alert" className="rounded-2xl border border-danger bg-danger-subtle p-4 text-sm text-danger">
        <p>{error}</p>
        <button type="button" onClick={onRetry} className={`${buttonClass} mt-3 border border-danger`}>
          Try again
        </button>
      </div>
    );
  }
  if (!problems?.length) {
    return (
      <div className="grid min-h-32 place-items-center p-6 text-center">
        <div>
          <UserCheck className="mx-auto h-7 w-7 text-neutral-400" aria-hidden="true" />
          <p className="mt-2 font-medium">No reported problems</p>
          <p className="mt-1 text-sm text-neutral-500">Riders can report a problem without cancelling their delivery.</p>
        </div>
      </div>
    );
  }
  return (
    <section aria-labelledby="assignment-problems-title">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden="true" />
        <div>
          <h2 id="assignment-problems-title" className="font-semibold">Reported problems</h2>
          <p className="mt-1 text-sm leading-5 text-neutral-500">
            Each delivery below stays assigned until you decide. Reporting a problem never
            cancels work on its own.
          </p>
        </div>
      </div>
      <ul className="mt-4 grid gap-3">
        {problems.map((problem) => (
          <ProblemRow key={problem.id} problem={problem} busy={busy} onResolve={onResolve} />
        ))}
      </ul>
    </section>
  );
}
